"use client";
import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Drawer,
  Grid,
  Typography,
  Button,
  useMediaQuery,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

import EventCard from "../../Elements/Card";
import CustomSearchBar from "../../Elements/CustomSearchBar";
import { useAppState } from "../../../appContext";
import ChecklistFilter from "../Filters/ChecklistFilter";

import EventAccordion from "./EventAccordion";
import { useRouter } from "next/router";
import { EventDetails } from "./EventDetails";
import { useTheme } from "@mui/material/styles";
import AppliedFilters from "../Filters/AppliedFilters";
import LocationFilter from "../Filters/LocationFilter";
import DeleteIcon from "@mui/icons-material/Delete";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { useNotification } from "../../Notification";
import { useFetchEvents } from "../../../hooks";
import isEqual from "lodash/isEqual";
import YearFilter from "../Filters/YearFilter";
import DateFilter from "../Filters/DateFilter";
import moment from "moment";
import { Timestamp } from "firebase/firestore";
import { useDebounce } from "../../../utils";
import VenueFilter from "../Filters/VenueFilter";
import firebase from "firebase/compat/app";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "@/utils/firebase-config";
import BandFilter from "../Filters/BandFilter";
import useIsSmallScreen from "./../../../utils/useIsSmallScreen";
import UseMyLocationButton from "../../Elements/UseMyLocationButton";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open, drawerWidth }) => ({
    flexGrow: 1,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: open ? drawerWidth : 0, // ✅ Fix left margin when closed
  })
);

const getDistanceMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const ANNAPOLIS_LOCATION = {
  lat: 38.96772105330125,
  lon: -76.51525587650268,
  miles: 20,
};
const LONGISLAND_LOCATION = {
  lat: 40.79157345621614,
  lon: -73.34159238491179,
  miles: 50,
};
const NYC_LOCATION = {
  lat: 40.7128,
  lon: -74.006,
  miles: 20,
};
const BALTIMORE_LOCATION = {
  lat: 39.2904,
  lon: -76.6122,
  miles: 25,
};
const OCEAN_CITY_LOCATION = {
  lat: 38.3365,
  lon: -75.0846,
  miles: 35,
};

const attributes = [
  {
    label: "Title",
    name: "title",
    sortable: true,
  },
  {
    label: "Description",
    name: "eventdescription",
    component: {
      type: "html",
    },
  },
  {
    label: "Event Date",
    name: "main_date",
    sortable: true,
    component: {
      type: "dateTime",
    },
  },
  {
    label: "Bands",
    name: "bands",
    state: "bands",
    component: {
      type: "array",
      contextProp: "bandname",
      queryParam: "bandId",
    },
  },
  {
    label: "Venue",
    name: "venue",
    state: "venues",
    component: {
      type: "object",
      contextProp: "venue_name",
      queryParam: "venueId",
    },
  },
];

export default function EventsLayout() {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const locationParam = router.query.location;
  const { eventId } = router.query;
  const [state, dispatch] = useAppState();
  const [searchTerm, setSearchTerm] = useState("");
  const theme = useTheme();
  const { showSuccessMessage, showInformationMessage } = useNotification();
  const [queryStrings, setQueryString] = useState([]);
  const [paginationProps, setPaginationProps] = useState({ skip: 0, take: 20 });
  const [localPagination, setLocalPagination] = useState({ skip: 0, take: 20 });
  const [currentPage, setCurrentPage] = useState(0);
  const debouncedSearchValue = useDebounce(searchTerm, 500);
  const [enableApi, setEnableApi] = useState(false);
  const [savedFilters, setSavedFilters] = useState(null);
  const isSmallScreen = useIsSmallScreen();
  const [ignoreFilters, setIgnoreFilters] = useState(false);
  const [highlightedEvents, setHighlightedEvents] = useState([]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const filters = JSON.parse(localStorage.getItem("applied_filters"));
      if (filters) {
        setSavedFilters(filters);
      }
    }
  }, []);

  useEffect(() => {
    if (savedFilters && typeof savedFilters === "object") {
      setFilters((prev) => ({
        ...prev,
        ...savedFilters,
      }));
    }
  }, [savedFilters]);

  const handleClearAll = () => {
    setFilters({
      bands: [],
      venues: [],
      states: [],
      date: [],
      coordinates: [
        { locationType: "default", miles: 0, zip: "", isValid: false },
      ],
    });
  };

  const isTabletScreen = useMediaQuery(
    "(min-width: 600px) and (max-width: 900px)"
  );
  const drawerWidth = isTabletScreen ? 300 : 400;
  const initialFilterState = {
    bands: [],
    venues: [],
    states: [],
    date: [],
    coordinates: [
      { locationType: "default", miles: 20, zip: "", isValid: false },
    ],
  };
  const [filters, setFilters] = useState({
    bands: [],
    venues: [],
    states: [],
    date: [],
    coordinates: [
      { locationType: "default", miles: 20, zip: "", isValid: false },
    ],
  });
  useEffect(() => {
    const fetchHighlighted = async () => {
      const stored = localStorage.getItem("location_filter");
      const filter = stored ? JSON.parse(stored) : null;

      const now = new Date();

      const q = query(
        collection(db, "events"),
        where("highlight", "==", true),
        where("main_date", ">=", now),
        orderBy("main_date", "asc")
      );

      const snapshot = await getDocs(q);
      snapshot.forEach((doc) => {});
      const allEvents = snapshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
      let nearby = allEvents;

      if (filter?.latitude && filter?.longitude && filter?.miles) {
        nearby = allEvents.filter((event) => {
          if (!event.coordinates) return false;
          const dist = getDistanceMiles(
            filter.latitude,
            filter.longitude,
            event.coordinates.latitude,
            event.coordinates.longitude
          );
          return dist <= filter.miles;
        });
      }

      setHighlightedEvents(nearby.slice(0, 5));
      // setHighlightedEvents(allEvents.slice(0, 5));
    };
    fetchHighlighted();
  }, []);

  useEffect(() => {
    if (!router.isReady) return;

    const locationParam = router.query.location;
    let newCoordinates = null;

    switch (locationParam) {
      case "annapolis":
        newCoordinates = ANNAPOLIS_LOCATION;
        break;
      case "long-island":
        newCoordinates = LONGISLAND_LOCATION;
        break;
      case "nyc":
        newCoordinates = NYC_LOCATION;
        break;
      case "baltimore":
        newCoordinates = BALTIMORE_LOCATION;
        break;
      case "ocean-city":
        newCoordinates = OCEAN_CITY_LOCATION;
        break;
    }

    if (newCoordinates) {
      const areCoordsDifferent =
        !filters.coordinates[0] ||
        filters.coordinates[0].longitude !== newCoordinates.lon ||
        filters.coordinates[0].latitude !== newCoordinates.lat ||
        filters.coordinates[0].miles !== newCoordinates.miles;

      if (areCoordsDifferent) {
        setFilters((prev) => ({
          ...prev,
          coordinates: [
            {
              locationType: "query",
              miles: newCoordinates.miles,
              longitude: newCoordinates.lon,
              latitude: newCoordinates.lat,
              isValid: true,
            },
          ],
        }));
      }
    } else if (
      locationParam &&
      filters.coordinates[0]?.locationType === "query"
    ) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        coordinates: [initialFilterState.coordinates[0]], // Reset to default
      }));
    } else if (
      !locationParam &&
      filters.coordinates[0]?.locationType === "query"
    ) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        coordinates: [initialFilterState.coordinates[0]], // Reset to default
      }));
    }
    localStorage.removeItem("applied_filters");
  }, [router.isReady, router.query.location]);

  useEffect(() => {
    const hasCoordinates =
      filters.coordinates?.[0]?.isValid &&
      filters.coordinates?.[0]?.miles > 0 &&
      filters.coordinates?.[0]?.longitude &&
      filters.coordinates?.[0]?.latitude;

    const hasStandardFilters =
      queryStrings.length > 0 &&
      !queryStrings.every((q) => q.field === "coordinates");

    const isJustDefault = filters.coordinates[0]?.locationType === "default";

    // ✅ Enable API if any useful filter is present
    if (hasCoordinates || hasStandardFilters) {
      setEnableApi(true);
    } else if (isJustDefault) {
      // ✅ Also enable by default to show all future events
      setEnableApi(true);
    } else {
      setEnableApi(false);
    }
  }, [queryStrings, filters.coordinates, savedFilters]);

  const getFilteredDate = (years) => {
    // ... (your existing getFilteredDate function) ...
  };

  useEffect(() => {
    const selectedDate = new Date(filters.date[0]);
    const startOfDay = new Date(
      Date.UTC(
        selectedDate.getUTCFullYear(),
        selectedDate.getUTCMonth(),
        selectedDate.getUTCDate(),
        5,
        0,
        0,
        0
      )
    );

    const endOfDay = new Date(
      Date.UTC(
        selectedDate.getUTCFullYear(),
        selectedDate.getUTCMonth(),
        selectedDate.getUTCDate(),
        28,
        59,
        59,
        999
      )
    );
    // ... (your existing useEffect for date filtering) ...
  }, [filters.date]);

  useEffect(() => {
    let queryStr = [
      ...(debouncedSearchValue?.length > 0
        ? [
            {
              field: "searchTerms",
              operator: "array-contains-any",
              value: debouncedSearchValue.toLowerCase().split(" "),
            },
          ]
        : []),
      ...(filters.bands && filters.bands.length > 0
        ? [
            {
              field: "bandIds",
              operator: "array-contains-any",
              value: filters.bands.map((v) => v.id),
            },
          ]
        : []),
      ...(filters.venues && filters.venues.length > 0
        ? [
            {
              field: "venue",
              operator: "in",
              value: filters.venues.map((v) => v.id),
            },
          ]
        : []),
      ...(filters.states && filters.states.length > 0
        ? [
            {
              field: "state",
              operator: "in",
              value: filters.states,
            },
          ]
        : []),
      ...(filters.date && filters.date.length > 0
        ? [
            {
              field: "main_date",
              operator: ">=",
              value: firebase.firestore.Timestamp.fromDate(
                new Date(
                  Date.UTC(
                    new Date(filters.date[0]).getUTCFullYear(),
                    new Date(filters.date[0]).getUTCMonth(),
                    new Date(filters.date[0]).getUTCDate(),
                    5,
                    0,
                    0,
                    0
                  )
                )
              ),
            },
            {
              field: "main_date",
              operator: "<=",
              value: firebase.firestore.Timestamp.fromDate(
                new Date(
                  Date.UTC(
                    new Date(filters.date[0]).getUTCFullYear(),
                    new Date(filters.date[0]).getUTCMonth(),
                    new Date(filters.date[0]).getUTCDate(),
                    28,
                    59,
                    59,
                    999
                  )
                )
              ),
            },
          ]
        : []),
      ...(filters?.coordinates.length > 0 &&
      filters?.coordinates[0]?.miles > 0 &&
      filters?.coordinates[0]?.longitude &&
      filters?.coordinates[0]?.latitude &&
      filters?.coordinates[0]?.isValid
        ? [
            {
              field: "coordinates",
              operator: "n/a",
              value: filters?.coordinates,
            },
          ]
        : []),
    ];
    if (!isEqual(queryStr, queryStrings)) {
      setQueryString([...queryStr]);
      setCurrentPage(0);
    }
  }, [filters, debouncedSearchValue, locationParam]); // Include searchParams in the dependency array

  useEffect(() => {
    // ... (your existing useEffect for enabling the API) ...
  }, [queryStrings, filters.coordinates]); // Include filters.coordinates

  const memoizedFilters = useMemo(
    () =>
      ignoreFilters
        ? queryStrings.filter((f) => f.field === "searchTerms")
        : queryStrings,
    [ignoreFilters, queryStrings]
  );

  const { events, loading, totalItems, isPaginated } = useFetchEvents(
    paginationProps.take,
    paginationProps.skip,
    memoizedFilters,
    enableApi
  );
  const removeQueryParam = (paramToRemove) => {
    // Create a URLSearchParams object from the current search string
    const params = new URLSearchParams(searchParams.toString());
    // Remove the specified query parameter
    params.delete(paramToRemove);

    // Navigate to the same path but with the modified search parameters
    router.push(`${window.location.pathname}?${params.toString()}`);
  };

  const filtersToRender = [
    {
      title: "Filter by Distance",
      contextProp: "coordinates",
      // friendlyName: "cities",
      type: "distance",
      colorCode: {
        primary: "rgba(35,73,144,1)",
        secondary: "rgba(35,73,144,0.1)",
      },
    },
    {
      title: "Filter by Date",
      contextProp: "date",
      // friendlyName: "cities",
      type: "date",
      colorCode: {
        primary: "rgba(57,2,118,1)",
        secondary: "rgba(57,2,118,0.1)",
      },
    },
    {
      title: "Filter by Band",
      contextProp: "bands",
      friendlyName: "bandname",
      type: "bands",
      colorCode: {
        primary: "rgba(65,66,183,1)",
        secondary: "rgba(65,66,183,0.1)",
      },
    },
    {
      title: "Filter by Venue",
      contextProp: "venues",
      friendlyName: "venue_name",
      type: "venues",
      colorCode: {
        primary: "rgba(241,86,40,1)",
        secondary: "rgba(241,86,40,0.1)",
      },
    },

    {
      title: "Filter by State",
      contextProp: "states",
      friendlyName: "state",
      type: "checklist",
      colorCode: {
        primary: "rgba(10,172,0,1)",
        secondary: "rgba(10,172,0,0.1)",
      },
    },
    // {
    //   title: "Filter by City",
    //   contextProp: "cities",
    //   friendlyName: "cities",
    //   type: "checklist",
    //   colorCode: {
    //     primary: "rgba(107,0,123,1)",
    //     secondary: "rgba(107,0,123,0.1)",
    //   },
    // },
    // {
    //   title: "Filter by Year",
    //   contextProp: "years",
    //   // friendlyName: "cities",
    //   type: "year",
    //   colorCode: {
    //     primary: "rgba(57,2,118,1)",
    //     secondary: "rgba(57,2,118,0.1)",
    //   },
    // },
  ];
  const handleChange = (filterName) => (value) => {
    setPaginationProps({
      skip: 0,
      take: 20,
    });
    setFilters((prev) => ({ ...prev, [filterName.contextProp]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      ...initialFilterState,
      coordinates: [
        { locationType: "default", miles: 0, zip: "", isValid: false },
      ],
    });
  };

  // Dummy data for select options
  const getFilteredResults = () => {
    let searchResults = events.slice(
      localPagination.skip,
      localPagination.skip + localPagination.take
    );

    // Deduplicate results
    const uniqueResults = [
      ...new Map(searchResults.map((item) => [item.id, item])).values(),
    ];

    return uniqueResults;
  };

  const getFilterContextPropValue = (f) => {
    if (f.type === "checklist" || f.type === "venues" || f.type === "bands") {
      return filters[f.contextProp];
    } else if (f.type === "distance") {
      let val = filters[f.contextProp][0];
      if (
        (val?.locationType === "current" || val?.locationType === "default") &&
        val?.latitude &&
        val?.longitude &&
        val?.miles > 0
      ) {
        return filters[f.contextProp];
      } else if (
        val?.locationType === "zip" &&
        val?.latitude &&
        val?.longitude &&
        val?.miles > -1 &&
        val?.zip
      ) {
        return filters[f.contextProp];
      }

      return [];
    } else if (f.type === "year") {
      return filters[f.contextProp];
    } else return filters[f.contextProp];
  };
  const getAppliedFilterValues = () => {
    return (
      filtersToRender
        // .filter((x) => x.type === "checklist")
        .map((f) => ({
          selectedValues: getFilterContextPropValue(f),
          data:
            f.type === "date"
              ? state?.[f.contextProp]
              : state?.[f.contextProp]
              ? state?.[f.contextProp].map((b, i) => ({
                  id: b?.id
                    ? b?.id
                    : typeof b === "string"
                    ? b
                    : b[f.friendlyName],
                  name: typeof b === "string" ? b : b[f.friendlyName],
                }))
              : [],
          onChange: handleChange(f),

          ...f,
        }))
        .filter((x) => x.selectedValues.length > 0)
    );
  };
  const renderFilter = (filterName, expandState) => {
    switch (filterName?.type) {
      case "checklist": {
        return (
          <ChecklistFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={handleChange(filterName)}
            data={state?.[filterName.contextProp]?.map((b, i) => ({
              id: b?.id
                ? b?.id
                : typeof b === "string"
                ? b
                : b[filterName.friendlyName],
              name: typeof b === "string" ? b : b[filterName.friendlyName],
            }))}
          />
        );
      }
      case "distance": {
        const handleFilterUpdate = (newFilters) => {
          setFilters((prev) => ({
            ...prev,
            coordinates: newFilters,
          }));
          setPaginationProps({ skip: 0, take: 20 });
        };
        return (
          <LocationFilter
            _value={
              filters?.[filterName?.contextProp]?.[0]?.locationType
                ? filters?.[filterName?.contextProp]
                : [{ locationType: "default", miles: 0, zip: "" }]
            }
            onUpdate={handleFilterUpdate}
          />
        );
      }
      case "year": {
        return (
          <YearFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={handleChange(filterName)}
          />
        );
      }
      case "date": {
        return (
          <DateFilter
            value={filters?.[filterName?.contextProp][0]}
            onChange={handleChange(filterName)}
          />
        );
      }
      case "venues": {
        return (
          <VenueFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={handleChange(filterName)}
            isExpanded={expandState}
          />
        );
      }
      case "bands": {
        return (
          <BandFilter
            colors={filterName?.colorCode}
            selectedItems={filters?.[filterName?.contextProp]}
            onChange={handleChange(filterName)}
            isExpanded={expandState}
          />
        );
      }
    }
  };
  const [expandedState, setExpandedState] = useState({}); // State to track expanded status of each card

  const handleExpandChange = (filterName, expanded) => {
    setExpandedState((prevState) => ({
      ...prevState,
      [filterName]: expanded, // Update the expanded status for the specific filter
    }));
  };
  const getFiltersToRender = () => {
    if (!filtersToRender || filtersToRender.length === 0) return null;
    return (
      <>
        <AppliedFilters
          filters={getAppliedFilterValues()}
          handleClearAll={clearAllFilters}
          ignoreFilters={ignoreFilters}
        />

        <Box sx={{ display: "flex", width: "100%" }}>
          {/* Parent container */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              mb: "16px",
              p: "0 8px",
              gap: 2,
            }}
          >
            {" "}
            {/* Your Box */}
            <Button
              startIcon={<DeleteIcon />}
              variant="outlined"
              color="error"
              sx={{ flexGrow: 1 }}
              // disabled={!localStorage.getItem("applied_filters")}
              onClick={() => {
                localStorage.removeItem("applied_filters");
                showInformationMessage("Saved Filters deleted successfully");
              }}
            >
              Delete
            </Button>
            <Button
              startIcon={<LibraryAddIcon />}
              variant="contained"
              sx={{ flexGrow: 1 }}
              disabled={getAppliedFilterValues().length <= 0}
              onClick={() => {
                localStorage.setItem(
                  "applied_filters",
                  JSON.stringify(filters)
                );
                if (localStorage.getItem("applied_filters")) {
                  showSuccessMessage("Filters saved successfully");
                }
              }}
            >
              Save
            </Button>
          </Box>
        </Box>

        {filtersToRender.map((filterName, index) => (
          <Box
            key={filterName?.title + index}
            sx={{ margin: "0 8px 16px 8px" }}
          >
            <EventCard
              key={`normal-${index}`}
              colorCode={filterName?.colorCode?.primary}
              cardTitle={filterName.title}
              collapsible
              onExpandChange={(expanded) =>
                handleExpandChange(filterName, expanded)
              }
            >
              <Box
                // padding="0"
                style={{
                  backgroundColor: "#fbfbfd",
                }}
              >
                <Box>{renderFilter(filterName, expandedState[filterName])}</Box>
              </Box>
            </EventCard>
          </Box>
        ))}
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", paddingLeft: "20px", padding: "20px" }}>
      <Drawer
        variant={isSmallScreen ? "temporary" : "persistent"}
        open={true}
        onClose={() => setOpen(false)}
        sx={{
          width: isSmallScreen ? "100%" : drawerWidth,
          flexShrink: 0,
          zIndex: 1200,
          transition: "transform 0.3s ease-in-out",
          "& .MuiDrawer-paper": {
            //left: "4px",
            width: isSmallScreen ? "100%" : drawerWidth,
            boxSizing: "border-box",
            paddingTop: "90px",
            background: "#eef0f4",
            border: "none",
            borderTopLeftRadius: "8px", // ✅ Adds a rounded top-left corner
            top: "80px", // ✅ Adds space from the top
            right: "100px", // ✅ Ensures it aligns properly
            position: "fixed", // ✅ Keeps it in the right position
            padding: "2px", // ✅ Adds internal spacing
            marginLeft: "10px", // ✅ Adds margin from the left
            paddingLeft: "10px", // ✅ Adds internal spacing
            "&::-webkit-scrollbar": {
              display: "none",
            },
          },
          display: open ? "block" : "none",
        }}
      >
        {open && (
          <Button
            onClick={() => setOpen(!open)}
            variant="contained"
            endIcon={<FilterListIcon />}
            sx={{
              height: "48px",
              margin: "0 8px 16px 8px",
              backgroundColor: theme?.primaryColor,
              "&:hover": {
                backgroundColor: theme?.primaryHoverColor,
              },
            }}
          >
            {"Hide Search Filters"}
          </Button>
        )}
        {getFiltersToRender()}
        {
          <Box
            sx={{
              marginTop: "200px",
              display: { xs: "block", sm: "none", md: "none" },
            }}
          ></Box>
        }
      </Drawer>
      <Main
        sx={{
          display: isSmallScreen && open ? "none" : "block",
        }}
        open={open}
      >
        {/* H1 Header for SEO */}
        <h1>Your Ultimate Guide to Live Music Events</h1>
        <h2 className="small-heading">
          Explore live music events, concerts, and local gigs with Webtunes.
          <br />
          Discover your favorite bands, new venues, and upcoming shows near you.
        </h2>
        <Box
          sx={{
            textAlign: "left",
            mb: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            component="a"
            href="/events?location=long-island" // Example for Long Island
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Long Island
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events?location=nyc" // Example for NYC
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            NYC
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events?location=baltimore" // Example for Baltimore
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Baltimore
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events?location=annapolis"
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Annapolis
          </Typography>
          <Typography
            variant="h6"
            component="a"
            href="/events?location=ocean-city" // Example for Ocean City
            sx={{
              textDecoration: "none",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Ocean City
          </Typography>
        </Box>

        <UseMyLocationButton />

        <Box
          sx={{
            display: "flex",
            width: "100%",
            gap: { xs: "10px", sm: "30px" },
            flexDirection: { xs: "column-reverse", sm: "row" },
          }}
        >
          {!open && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" }, // Ensures the text wraps within the box
                  alignItems: "center",
                  wordWrap: "break-word", // Breaks long words
                  overflow: "hidden", // Prevents overflow issues
                  backgroundColor: "#f5f5f5", // Optional: to differentiate the box
                  padding: 1, // Adds padding for better appearance
                  borderRadius: "4px", // Optional: for rounded corners
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap", // Prevents text wrapping
                  }}
                >
                  Current Filter(s):
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1, // Occupies available space
                    minWidth: 300, // Prevents overflow
                  }}
                >
                  <AppliedFilters
                    filters={getAppliedFilterValues()}
                    handleClearAll={handleClearAll}
                    ignoreFilters={false} // Pass your ignore filters state
                  />
                </Box>
                {/* Search Events  */}
                <Button
                  onClick={() => setOpen(true)}
                  variant="contained"
                  endIcon={<FilterListIcon />}
                  sx={{
                    height: "48px",
                    width: { xs: "200px", sm: "250px" },
                    backgroundColor: theme?.primaryColor,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: theme?.primaryHoverColor,
                    },
                  }}
                >
                  {getAppliedFilterValues().length > 0 && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "white",
                        background: ignoreFilters ? "gray" : "red",
                        borderRadius: "50%",
                        paddingBottom: "3px",
                        height: "16px",
                        width: "16px",
                        marginRight: "5px",
                      }}
                    >
                      {getAppliedFilterValues().length}
                    </div>
                  )}
                  {"Add / Change Filters"}
                </Button>
                <Box
                  sx={{
                    minWidth: "300px", // Ensure a minimum width for the search bar
                    width: { xs: "100%", sm: "auto" }, // Full width on small screens, auto on larger screens
                    alignSelf: "center",
                  }}
                >
                  <CustomSearchBar
                    handleChange={(searchTerm) => setSearchTerm(searchTerm)}
                    placeholder="Search events..."
                  />
                </Box>
                {/* End Search Events  */}
              </Box>
            </Box>
          )}
        </Box>

        {highlightedEvents.length > 0 && (
          <Box sx={{ mt: 2, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              🎯 Highlighted Events Near You
            </Typography>
            <Grid container spacing={2}>
              {highlightedEvents.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <EventCard
                    key={`highlight-${event.id}`}
                    cardTitle={event.title}
                    colorCode="#ffd54f"
                    collapsible={false}
                  >
                    {event.imageUrl ? (
                      <img
                        src={event.imageUrl}
                        alt={event.title}
                        style={{
                          width: "100%",
                          maxHeight: 200,
                          objectFit: "cover",
                          marginBottom: 8,
                          borderRadius: 4,
                        }}
                      />
                    ) : event.bands &&
                      event.bands.length > 0 &&
                      event.bands[0].imageUrl ? (
                      <img
                        src={event.bands[0].imageUrl}
                        alt={event.bands[0].bandname || "Band image"}
                        style={{
                          width: "100%",
                          maxHeight: 200,
                          objectFit: "cover",
                          marginBottom: 8,
                          borderRadius: 4,
                        }}
                      />
                    ) : null}

                    {/* 🗓 Date */}
                    <Typography variant="body2" gutterBottom>
                      <strong>Date:</strong>{" "}
                      {event.main_date?.seconds &&
                        new Date(
                          event.main_date.seconds * 1000
                        ).toLocaleString()}
                    </Typography>

                    {/* 📍 Venue */}
                    <Typography variant="body2" gutterBottom>
                      <strong>Venue:</strong> {event.venue_name}, {event.city}
                    </Typography>

                    {/* 🌐 Ticket link */}
                    {event.ticket_url && (
                      <Typography variant="body2" gutterBottom>
                        <strong>Tickets:</strong>{" "}
                        <a
                          href={event.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Buy Now
                        </a>
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      <a
                        href={`/events/${event.id}`}
                        style={{
                          textDecoration: "none",
                          color: "#1976d2",
                          fontWeight: "bold",
                        }}
                      >
                        View Event Details →
                      </a>
                    </Typography>
                    {/* 📝 Description */}
                    {event.eventdescription && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {event.eventdescription}
                      </Typography>
                    )}
                  </EventCard>

                  {/* <EventCard event={event} /> */}
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {events && (
          <EventAccordion
            ignoreFilters={ignoreFilters}
            setIgnoreFilters={() => setIgnoreFilters((i) => !i)}
            loading={loading}
            totalCount={totalItems}
            setPaginationProps={(skip, take) => {
              if (isPaginated) {
                setPaginationProps({
                  skip,
                  take,
                });
              } else {
                setLocalPagination({
                  skip,
                  take,
                });
              }
            }}
            currentPage={currentPage}
            setCurrentPage={(v) => setCurrentPage(v)}
            data={getFilteredResults()}
            attributes={attributes}
          />
        )}
        {events?.length === 0 && !loading && (
          <Typography
            sx={{
              justifyContent: "center",
              display: "flex",
            }}
          >
            No Data found
          </Typography>
        )}
        {eventId && events?.length > 0 && (
          <EventDetails
            event={events.find((e) => e.id == eventId)}
            onDrawerClose={() => {
              removeQueryParam("eventId");
            }}
          />
        )}
      </Main>
    </Box>
  );
}
